import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { AlertCircle, CheckCircle2, Info, AlertTriangle, X } from 'lucide-react';
import { cn } from '@utils/cn';

/**
 * Alert variants using class-variance-authority
 */
const alertVariants = cva(
  'relative w-full rounded-lg border p-4 [&>svg~*]:pl-7 [&>svg+div]:translate-y-[-3px] [&>svg]:absolute [&>svg]:left-4 [&>svg]:top-4 [&>svg]:text-foreground',
  {
    variants: {
      variant: {
        default: 'bg-background text-foreground',
        success: 'border-success/50 text-success bg-success/10 [&>svg]:text-success',
        warning: 'border-warning/50 text-warning bg-warning/10 [&>svg]:text-warning',
        error: 'border-destructive/50 text-destructive bg-destructive/10 [&>svg]:text-destructive',
        info: 'border-info/50 text-info bg-info/10 [&>svg]:text-info',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

/**
 * Icon mapping for each alert variant
 */
const variantIconMap = {
  default: Info,
  success: CheckCircle2,
  warning: AlertTriangle,
  error: AlertCircle,
  info: Info,
};

export interface AlertProps
  extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof alertVariants> {
  /**
   * Make alert dismissible
   */
  dismissible?: boolean;
  /**
   * Callback when alert is dismissed
   */
  onDismiss?: () => void;
  /**
   * Custom icon to override the default variant icon
   */
  icon?: React.ReactNode;
  /**
   * Hide the icon completely
   */
  hideIcon?: boolean;
}

/**
 * Alert Component
 *
 * Display important messages and notifications to users with appropriate visual emphasis.
 * Supports multiple severity levels and dismissible functionality.
 *
 * @example
 * // Basic alerts
 * <Alert variant="success">
 *   <AlertTitle>Success</AlertTitle>
 *   <AlertDescription>Patient record saved successfully.</AlertDescription>
 * </Alert>
 *
 * @example
 * // Dismissible alert
 * <Alert variant="warning" dismissible onDismiss={() => logger.log('dismissed')}>
 *   <AlertTitle>Warning</AlertTitle>
 *   <AlertDescription>Blood pressure is elevated.</AlertDescription>
 * </Alert>
 *
 * @example
 * // Alert with custom icon
 * <Alert icon={<HeartIcon />}>
 *   <AlertTitle>Custom Icon</AlertTitle>
 *   <AlertDescription>Heart rate is normal.</AlertDescription>
 * </Alert>
 *
 * @example
 * // Error alert
 * <Alert variant="error">
 *   <AlertTitle>Error</AlertTitle>
 *   <AlertDescription>
 *     Failed to load patient data. Please try again.
 *   </AlertDescription>
 * </Alert>
 *
 * @example
 * // Info alert without icon
 * <Alert variant="info" hideIcon>
 *   <AlertDescription>
 *     Appointment scheduled for tomorrow at 10:00 AM
 *   </AlertDescription>
 * </Alert>
 */
const Alert = React.forwardRef<HTMLDivElement, AlertProps>(
  (
    { className, variant = 'default', dismissible, onDismiss, icon, hideIcon, children, ...props },
    ref
  ) => {
    const [dismissed, setDismissed] = React.useState(false);

    const handleDismiss = () => {
      setDismissed(true);
      onDismiss?.();
    };

    if (dismissed) return null;

    // Get the default icon for the variant
    const IconComponent = variantIconMap[variant || 'default'];
    const displayIcon = icon || <IconComponent className="h-4 w-4" />;

    return (
      <div ref={ref} role="alert" className={cn(alertVariants({ variant }), className)} {...props}>
        {!hideIcon && displayIcon}
        <div className="flex w-full items-start justify-between gap-2">
          <div className="flex-1">{children}</div>
          {dismissible && (
            <button
              type="button"
              onClick={handleDismiss}
              className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md hover:bg-black/10 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
              aria-label="Dismiss alert"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
    );
  }
);
Alert.displayName = 'Alert';

/**
 * Alert Title Component
 */
const AlertTitle = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLHeadingElement>>(
  ({ className, children, ...props }, ref) => (
    <h5
      ref={ref}
      className={cn('mb-1 font-medium leading-none tracking-tight', className)}
      {...props}
    >
      {children}
    </h5>
  )
);
AlertTitle.displayName = 'AlertTitle';

/**
 * Alert Description Component
 */
const AlertDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn('text-sm [&_p]:leading-relaxed', className)} {...props} />
));
AlertDescription.displayName = 'AlertDescription';

/**
 * Inline Alert Component
 *
 * A compact inline alert for form validation and inline messages
 */
export interface InlineAlertProps extends Omit<AlertProps, 'dismissible' | 'onDismiss'> {
  message: string;
}

/**
 * InlineAlert Component
 *
 * A simplified alert component for inline messages and form validation.
 *
 * @example
 * <InlineAlert variant="error" message="This field is required" />
 * <InlineAlert variant="success" message="Changes saved" />
 */
const InlineAlert = React.forwardRef<HTMLDivElement, InlineAlertProps>(
  ({ className, variant, message, icon, hideIcon, ...props }, ref) => {
    const IconComponent = variantIconMap[variant || 'default'];
    const displayIcon = icon || <IconComponent className="h-4 w-4" />;

    return (
      <div
        ref={ref}
        role="alert"
        className={cn(
          'flex items-center gap-2 rounded-md p-2 text-sm',
          variant === 'success' && 'bg-success/10 text-success',
          variant === 'warning' && 'bg-warning/10 text-warning',
          variant === 'error' && 'bg-destructive/10 text-destructive',
          variant === 'info' && 'bg-info/10 text-info',
          variant === 'default' && 'bg-muted text-foreground',
          className
        )}
        {...props}
      >
        {!hideIcon && <span className="shrink-0">{displayIcon}</span>}
        <span>{message}</span>
      </div>
    );
  }
);
InlineAlert.displayName = 'InlineAlert';

/**
 * Toast-like Alert Component for notifications
 */
export interface AlertNotificationProps extends AlertProps {
  /**
   * Auto-dismiss after specified milliseconds
   */
  autoClose?: number;
}

/**
 * AlertNotification Component
 *
 * An alert that auto-dismisses after a specified time, useful for toast-like notifications.
 *
 * @example
 * <AlertNotification
 *   variant="success"
 *   autoClose={3000}
 *   dismissible
 * >
 *   <AlertDescription>Patient record updated successfully</AlertDescription>
 * </AlertNotification>
 */
const AlertNotification = React.forwardRef<HTMLDivElement, AlertNotificationProps>(
  ({ autoClose, onDismiss, dismissible = true, ...props }, ref) => {
    const [dismissed, setDismissed] = React.useState(false);

    React.useEffect(() => {
      if (autoClose) {
        const timer = setTimeout(() => {
          setDismissed(true);
          onDismiss?.();
        }, autoClose);

        return () => clearTimeout(timer);
      }
      return undefined;
    }, [autoClose, onDismiss]);

    if (dismissed) return null;

    return (
      <Alert
        ref={ref}
        dismissible={dismissible}
        onDismiss={() => {
          setDismissed(true);
          onDismiss?.();
        }}
        {...props}
      />
    );
  }
);
AlertNotification.displayName = 'AlertNotification';

export { Alert, AlertTitle, AlertDescription, InlineAlert, AlertNotification, alertVariants };
